/* eslint-disable react/require-default-props */
import React, { useMemo, useCallback } from 'react';
import proptypes from 'prop-types';
import { useImpression, ImpressionProvider } from '@thd-olt-component-react/impression';
import { BayImage } from './BayImage';

const { number, shape, bool, string } = proptypes;

// This Component is responsible for adding the useImpressions hook for this bay.
const BentoBay = React.memo(({
  bentoBayData = {},
  position = 1,
  isSponsored = false,
  enableB2CRadius = false
}) => {
  const isHotspotImage = useMemo(() => !!bentoBayData?.previewImage?.imageHotspot?.id
    || !!bentoBayData?.components?.[0]?.previewImage?.imageHotspot?.id
    || !!bentoBayData?.sourceData?.desktop?.hotspot, [bentoBayData]);

  const impressionId = useMemo(() => bentoBayData?.id || bentoBayData?.sys?.id,
    [bentoBayData]
  );

  const hero = useMemo(() => bentoBayData?.components?.[0] || bentoBayData,
    [bentoBayData]
  );

  const containsMp4 = useCallback((entry) => {
    const previewUrl = entry?.previewImage?.damDownloadedContent?.url
      || entry?.previewImage?.damContentSelector?.assetData?.[0]?.selectedImageUrl
      || '';
    const mobileUrl = entry?.mobilePreviewImage?.damDownloadedContent?.url
      || entry?.mobilePreviewImage?.damContentSelector?.assetData?.[0]?.selectedImageUrl
      || '';
    return previewUrl.includes('.mp4') || mobileUrl.includes('.mp4');
  }, []);

  const { ref, clickID } = useImpression({
    data: {
      id: impressionId || 'na',
      component: 'BentoBay',
      name: 'BentoBay',
      type: isSponsored ? 'sponsoredcontent' : 'content',
      position,
    },
  });

  const impressionData = useMemo(() => ({
    id: impressionId,
    component: 'BentoHero',
    name: 'BentoHero',
    type: 'content'
  }), [impressionId]);

  return (
    <div data-component="BentoBay" className="sui-h-full sui-w-full">
      {isHotspotImage ? (
        <ImpressionProvider data={impressionData}>
          <div
            ref={ref}
            // eslint-disable-next-line react/no-unknown-property
            clickid={clickID}
            className="sui-h-full"
          >
            <BayImage
              bayImageData={bentoBayData}
              isHotspotImage={isHotspotImage}
              position={position}
              hrefLink={hero?.link}
              isVideo={containsMp4(hero)}
              enableB2CRadius={enableB2CRadius}
            />
          </div>
        </ImpressionProvider>
      ) : (
        <a
          aria-label={`Navigate to ${bentoBayData?.altText
            || bentoBayData?.components?.[0]?.altText
            || 'No description available'
          }`}
          href={hero?.link}
          target="_self"
          // eslint-disable-next-line react/no-unknown-property
          clickid={clickID}
          ref={ref}
        >
          <BayImage
            bayImageData={bentoBayData}
            isHotspotImage={isHotspotImage}
            position={position}
            hrefLink={hero?.link}
            isVideo={containsMp4(hero)}
            enableB2CRadius={enableB2CRadius}
          />
        </a>
      )}
    </div>
  );
});

BentoBay.propTypes = {
  bentoBayData: shape({}),
  position: number,
  isSponsored: bool,
  enableB2CRadius: bool,
};

BentoBay.displayName = 'BentoBay';

function BentoBayRenderer({
  position,
  isSponsored,
  enableB2CRadius,
  componentId: _componentId,
  componentClass: _componentClass,
  slotID: _slotID,
  slotName: _slotName,
  slotNumber: _slotNumber,
  ...bayData
}) {
  return (
    <BentoBay
      bentoBayData={bayData}
      position={position}
      isSponsored={isSponsored}
      enableB2CRadius={enableB2CRadius}
    />
  );
}

BentoBayRenderer.propTypes = {
  position: number,
  isSponsored: bool,
  enableB2CRadius: bool,
  componentId: string,
  componentClass: string,
  slotID: string,
  slotName: string,
  slotNumber: number,
};

BentoBayRenderer.defaultProps = {
  position: 1,
  isSponsored: false,
  enableB2CRadius: false,
};

export { BentoBay, BentoBayRenderer };